import React, { useState, useEffect, useRef, useCallback } from "react";

interface GlobalFooterProps {
  shouldRetract?: boolean;
}

const GlobalFooter: React.FC<GlobalFooterProps> = ({ shouldRetract = false }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    // Match header timing
    const timer = setTimeout(() => setHasMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollYRef = useRef(0);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const resetHideTimer = useCallback(() => {
    clearHideTimer();

    hideTimerRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 3000);
  }, [clearHideTimer]);

  const showFooter = useCallback(() => {
    setIsVisible(true);
    resetHideTimer();
  }, [resetHideTimer]);

  useEffect(() => {
    if (shouldRetract) {
      resetHideTimer();
    } else {
      setIsVisible(true);
      clearHideTimer();
    }

    const handleScroll = () => {
      if (!shouldRetract) return;

      const currentScrollY = window.scrollY;
      const scrollDiff = currentScrollY - lastScrollYRef.current;

      if (scrollDiff < -10) {
        // Scrolling up - show and reset timer
        showFooter();
      } else if (scrollDiff > 10) {
        // Scrolling down - hide immediately
        setIsVisible(false);
        clearHideTimer();
      }

      lastScrollYRef.current = currentScrollY;
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!shouldRetract) return;

      // Show footer on mouse move if currently hidden and mouse is near bottom
      const windowHeight = window.innerHeight;
      if (!isVisible && e.clientY > windowHeight - 10) {
        showFooter();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("mousemove", handleMouseMove, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleMouseMove);
      clearHideTimer();
    };
  }, [isVisible, showFooter, resetHideTimer, clearHideTimer, shouldRetract]);

  return (
    <footer
      className={`fixed bottom-0 left-0 right-0 z-[60] bg-background shadow-elegant border-t border-border/50
        transition-all duration-1000 ease-in-out
        ${!hasMounted ? "translate-y-full" : isVisible ? "translate-y-0" : "translate-y-[calc(100%-8px)]"}
      `}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between px-6 py-3 gap-2">
        <div className="text-sm text-muted-foreground text-left">
          © 2025 Carline Consensor, LLC.<span className="hidden md:inline"> All rights reserved.</span>
        </div>

        <div className="text-sm text-muted-foreground text-right md:text-left">
          Contact us:{" "}
          <a href="mailto:contact@carlineconsensor.com" className="text-primary hover:underline">
            contact@carlineconsensor.com
          </a>
        </div>
      </div>
    </footer>
  );
};

export default GlobalFooter;
